import {
  Currency,
  type CurrencyConstructor,
} from '@webapp/core/entities/external/Currency'
import {
  Approver,
  type ApproverConstructor,
} from '@webapp/core/entities/internal/Approver'
import {
  User,
  type UserConstructorData,
} from '@webapp/core/entities/internal/User'
import {
  TravelStatuses,
  type TravelStatusEnum,
} from '../enums/TravelStatusEnum'
import {
  NextApproverChoice,
  type NextApproverChoiceConstructor,
} from '../NextApproverChoice'
import {
  TravelApprovalLog,
  type TravelApprovalLogConstructor,
} from '../TravelApprovalLog'
import {
  TravelDestination,
  type TravelDestinationConstructor,
} from '../TravelDestination'
import { type TravelCategoryReference } from '../TravelRequestSummary'

export interface TravelRequestDetailConstructor {
  id: number
  uuid: string
  status: TravelStatusEnum
  travelBudget: number
  purpose: string
  details: Nullable<string>
  dateCreated: ISODateString
  dateLastModified: ISODateString
  canEdit: boolean
  canDelete: boolean
  canApprove: boolean
  currency: CurrencyConstructor
  requester: UserConstructorData
  // Null once the travel is approved or denied
  nextApprover: Nullable<ApproverConstructor>
  // Set when the travel was routed to a delegate of its next approver
  approvalDelegateeId: Nullable<number>
  // Null when the department has no location (`Department.branch` is nullable)
  primaryCategory: Nullable<TravelCategoryReference>
  // `Travel.department` is a non-null FK; its id feeds the edit PUT payload
  secondaryCategory: TravelCategoryReference
  destinations: TravelDestinationConstructor[]
  logs: TravelApprovalLogConstructor[]
  // Empty when approving completes the routing
  nextApproverChoices: NextApproverChoiceConstructor[]
}

export class TravelRequestDetail {
  readonly id: number
  readonly uuid: string
  readonly status: TravelStatusEnum
  readonly travelBudget: number
  readonly purpose: string
  readonly details: Nullable<string>
  readonly dateCreated: Date
  readonly dateLastModified: Date
  readonly canEdit: boolean
  readonly canDelete: boolean
  readonly canApprove: boolean
  readonly currency: Currency
  readonly requester: User
  readonly nextApprover: Nullable<Approver>
  readonly approvalDelegateeId: Nullable<number>
  readonly primaryCategory: Nullable<TravelCategoryReference>
  readonly secondaryCategory: TravelCategoryReference
  readonly destinations: TravelDestination[]
  readonly logs: TravelApprovalLog[]
  readonly nextApproverChoices: NextApproverChoice[]

  constructor(data: TravelRequestDetailConstructor) {
    this.id = data.id
    this.uuid = data.uuid
    this.status = data.status
    this.travelBudget = data.travelBudget
    this.purpose = data.purpose
    this.details = data.details
    this.dateCreated = new Date(data.dateCreated)
    this.dateLastModified = new Date(data.dateLastModified)
    this.canEdit = data.canEdit
    this.canDelete = data.canDelete
    this.canApprove = data.canApprove
    this.currency = new Currency(data.currency)
    this.requester = new User(data.requester)
    this.nextApprover = data.nextApprover
      ? new Approver(data.nextApprover)
      : null
    this.approvalDelegateeId = data.approvalDelegateeId
    this.primaryCategory = data.primaryCategory
    this.secondaryCategory = data.secondaryCategory
    this.destinations = data.destinations.map(
      (destination) => new TravelDestination(destination)
    )
    this.logs = data.logs.map((log) => new TravelApprovalLog(log))
    this.nextApproverChoices = data.nextApproverChoices.map(
      (choice) => new NextApproverChoice(choice)
    )
  }

  // Computed properties
  get isPending(): boolean {
    return this.status === TravelStatuses.PENDING
  }

  get hasPurpose(): boolean {
    return this.purpose.trim().length > 0
  }

  get hasDetails(): boolean {
    return this.details !== null && this.details.trim().length > 0
  }

  get hasLogs(): boolean {
    return this.logs.length > 0
  }

  get isApprovable(): boolean {
    return this.canApprove && this.isPending && this.canEdit
  }

  // Approving completes the routing: there is no next approver to choose
  get isFinalApproval(): boolean {
    return this.nextApproverChoices.length === 0
  }

  // Methods
  public isRequestedBy(userId: number): boolean {
    return this.requester.id === userId
  }
}
